import { useState, useMemo, useRef, useEffect, useCallback } from "react"
import { createPortal } from "react-dom"
import { useNavigate } from "react-router-dom"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { SiteHeader } from "@/components/site-header"
import { authorizedFetch } from "@/lib/api"
import { API_URL } from "@/lib/mutation-utils"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import {
  Dialog, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog"
import { SearchIcon, XIcon, ChevronsUpDownIcon, GlobeIcon, MapPinIcon, ArrowLeftRightIcon } from "lucide-react"
import { cn } from "@/lib/utils"
import { usePermissions } from "@/hooks/use-permissions"
import MilestoneKanban, {
  type ShipmentMilestoneRow,
  type MilestoneDef,
} from "./MilestoneKanban"
import InternacionalShipmentCard, { type InternacionalCardRow } from "./InternacionalShipmentCard"

// ─── Milestones (8 steps) ─────────────────────────────────────────────────────

const INTERNACIONAL_MILESTONES: MilestoneDef[] = [
  { key: "originArrivalAt",               label: "Llegada al origen",          short: "En origen"        },
  { key: "originLoadingAt",               label: "Carga en origen",            short: "Cargando"         },
  { key: "customsOriginArrivalAt",        label: "Llegada aduana origen",      short: "Aduana origen"    },
  { key: "borderOriginArrivalAt",         label: "Llegada frontera origen",    short: "Frontera origen"  },
  { key: "borderDestinationArrivalAt",    label: "Llegada frontera destino",   short: "Frontera destino" },
  { key: "customsDestinationDepartureAt", label: "Salida aduana destino",      short: "Aduana destino"   },
  { key: "destinationUnloadingAt",        label: "Descarga en destino",        short: "Descarga destino" },
  { key: "finalizedAt",                   label: "Finalizado",                 short: "Finalizado"       },
]

// Implicit fields set/cleared alongside kanban milestone transitions.
const IMPLICIT_FIELDS: { trigger: string; implicits: string[] }[] = [
  { trigger: "customsOriginArrivalAt",        implicits: ["originDepartureAt"]                                            },
  { trigger: "borderOriginArrivalAt",         implicits: ["customsOriginDepartureAt"]                                     },
  { trigger: "borderDestinationArrivalAt",    implicits: ["borderOriginDepartureAt", "borderCrossingAt"]                  },
  { trigger: "customsDestinationDepartureAt", implicits: ["borderDestinationDepartureAt", "customsDestinationArrivalAt"]  },
  { trigger: "destinationUnloadingAt",        implicits: ["destinationArrivalAt"]                                         },
  { trigger: "finalizedAt",                   implicits: ["destinationUnloadingAt", "destinationArrivalAt"]               },
]

// ─── Location option ──────────────────────────────────────────────────────────

interface LocationOption {
  value:   string          // "country:Argentina" | "city:San Nicolas"
  label:   string
  type:    "country" | "city"
  country: string
}

// ─── MultiLocationSelect ──────────────────────────────────────────────────────

function MultiLocationSelect({
  options,
  selected,
  onChange,
  placeholder = "Filtrar...",
}: {
  options:     LocationOption[]
  selected:    string[]
  onChange:    (values: string[]) => void
  placeholder?: string
}) {
  const [open, setOpen]       = useState(false)
  const [search, setSearch]   = useState("")
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 })
  const triggerRef            = useRef<HTMLButtonElement>(null)
  const inputRef              = useRef<HTMLInputElement>(null)

  const calcPos = useCallback(() => {
    if (!triggerRef.current) return
    const r = triggerRef.current.getBoundingClientRect()
    setDropPos({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 220) })
  }, [])

  useEffect(() => {
    if (!open) return
    calcPos()
    function handleClick(e: MouseEvent) {
      const target = e.target as Node
      if (triggerRef.current?.contains(target)) return
      const portal = document.getElementById("multi-location-portal-active")
      if (portal?.contains(target)) return
      setOpen(false)
      setSearch("")
    }
    function handleReposition() { calcPos() }
    document.addEventListener("mousedown", handleClick)
    window.addEventListener("scroll", handleReposition, true)
    window.addEventListener("resize", handleReposition)
    return () => {
      document.removeEventListener("mousedown", handleClick)
      window.removeEventListener("scroll", handleReposition, true)
      window.removeEventListener("resize", handleReposition)
    }
  }, [open, calcPos])

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 10)
  }, [open])

  const filtered = useMemo(() => {
    const term = search.toLowerCase().trim()
    if (!term) return options
    return options.filter(o =>
      o.label.toLowerCase().includes(term) ||
      o.country.toLowerCase().includes(term)
    )
  }, [options, search])

  // Group by country
  const grouped = useMemo(() => {
    const map = new Map<string, LocationOption[]>()
    for (const o of filtered) {
      if (!map.has(o.country)) map.set(o.country, [])
      map.get(o.country)!.push(o)
    }
    return Array.from(map.entries())
  }, [filtered])

  function toggle(value: string) {
    onChange(selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value])
  }

  const selectedLabels = selected.map(v => options.find(o => o.value === v)?.label ?? v)

  const dropdown = open ? (
    <div
      id="multi-location-portal-active"
      style={{ position: "fixed", top: dropPos.top, left: dropPos.left, width: dropPos.width, zIndex: 9999 }}
      className="rounded-md border bg-popover text-popover-foreground shadow-md"
    >
      <div className="flex items-center border-b px-3">
        <SearchIcon className="size-3.5 shrink-0 text-muted-foreground mr-2" />
        <input
          ref={inputRef}
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Buscar ciudad o país..."
          className="flex h-9 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
        />
      </div>
      <div className="max-h-64 overflow-y-auto py-1">
        {grouped.length === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground">Sin resultados</div>
        ) : (
          grouped.map(([country, opts]) => (
            <div key={country}>
              {/* Country row */}
              <button
                type="button"
                onMouseDown={e => { e.preventDefault(); toggle(`country:${country}`) }}
                className={cn(
                  "flex w-full items-center gap-2 px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                  selected.includes(`country:${country}`) && "bg-accent/50 text-foreground",
                )}
              >
                <GlobeIcon className="size-3 shrink-0" />
                {country}
                {selected.includes(`country:${country}`) && (
                  <span className="ml-auto text-[10px] text-primary font-medium">✓</span>
                )}
              </button>
              {/* City rows */}
              {opts.filter(o => o.type === "city").map(o => (
                <button
                  key={o.value}
                  type="button"
                  onMouseDown={e => { e.preventDefault(); toggle(o.value) }}
                  className={cn(
                    "flex w-full items-center gap-2 px-3 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground pl-7",
                    selected.includes(o.value) && "bg-accent/50",
                  )}
                >
                  <MapPinIcon className="size-3 shrink-0 text-muted-foreground" />
                  <span className="flex-1 text-left">{o.label}</span>
                  {selected.includes(o.value) && (
                    <span className="text-[10px] text-primary font-medium">✓</span>
                  )}
                </button>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  ) : null

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => { if (!open) calcPos(); setOpen(v => !v); setSearch("") }}
        className={cn(
          "flex h-7 w-full items-center justify-between rounded-md border border-input bg-background px-2 py-1 text-xs",
          "ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
          open && "ring-2 ring-ring ring-offset-2",
        )}
      >
        <span className="truncate text-left min-w-0 flex-1">
          {selected.length === 0
            ? <span className="text-muted-foreground">{placeholder}</span>
            : selectedLabels.join(", ")
          }
        </span>
        <ChevronsUpDownIcon className="size-3.5 shrink-0 opacity-50 ml-1" />
      </button>
      {typeof document !== "undefined" && createPortal(dropdown, document.body)}
    </div>
  )
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function matchesLocation(
  cityName:    string | null,
  countryName: string | null,
  selected:    string[],
): boolean {
  if (selected.length === 0) return true
  return selected.some(v => {
    if (v.startsWith("country:")) return countryName === v.slice(8)
    if (v.startsWith("city:"))    return cityName    === v.slice(5)
    return false
  })
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const QUERY_KEY = ["shipments", "internacional"] as const

type PendingMilestoneChange = {
  id:       string
  updates:  Record<string, string | null>
  dateTime: string
}

function toLocalDateTimeInputValue(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, "0")
  return [
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
    `${pad(date.getHours())}:${pad(date.getMinutes())}`,
  ].join("T")
}

export default function CargasInternacionalPage() {
  const navigate    = useNavigate()
  const qc          = useQueryClient()
  const { can }     = usePermissions()
  const isAdmin     = can("write", "shipments")

  const [search,             setSearch]             = useState("")
  const [dateFrom,           setDateFrom]           = useState("")
  const [dateTo,             setDateTo]             = useState("")
  const [filterOrigins,      setFilterOrigins]      = useState<string[]>([])
  const [filterDestinations, setFilterDestinations] = useState<string[]>([])
  const [pendingMilestoneChange, setPendingMilestoneChange] = useState<PendingMilestoneChange | null>(null)

  // ── Fetch ────────────────────────────────────────────────────────────────────
  const { data, isLoading } = useQuery<ShipmentMilestoneRow[]>({
    queryKey: [...QUERY_KEY, dateFrom, dateTo],
    queryFn:  async () => {
      const params = new URLSearchParams({ workLine: "internacional", deleted: "false", pageSize: "500" })
      if (dateFrom) params.set("dateFrom", dateFrom)
      if (dateTo)   params.set("dateTo",   dateTo)
      const res  = await authorizedFetch(`${API_URL}/api/shipments?${params}`)
      const json = await res.json() as { data: ShipmentMilestoneRow[] }
      return json.data
    },
  })

  // ── Location options derived from loaded data ─────────────────────────────
  const originOptions = useMemo<LocationOption[]>(() => {
    if (!data) return []
    const countries = new Map<string, boolean>()
    const cities    = new Map<string, { country: string }>()
    for (const r of data) {
      if (r.senderCountryName) countries.set(r.senderCountryName, true)
      if (r.senderCityName && r.senderCountryName)
        cities.set(r.senderCityName, { country: r.senderCountryName })
    }
    const opts: LocationOption[] = []
    for (const [country] of countries)
      opts.push({ value: `country:${country}`, label: country, type: "country", country })
    for (const [city, { country }] of cities)
      opts.push({ value: `city:${city}`, label: city, type: "city", country })
    return opts.sort((a, b) => a.country.localeCompare(b.country) || a.label.localeCompare(b.label))
  }, [data])

  const destinationOptions = useMemo<LocationOption[]>(() => {
    if (!data) return []
    const countries = new Map<string, boolean>()
    const cities    = new Map<string, { country: string }>()
    for (const r of data) {
      if (r.recipientCountryName) countries.set(r.recipientCountryName, true)
      if (r.recipientCityName && r.recipientCountryName)
        cities.set(r.recipientCityName, { country: r.recipientCountryName })
    }
    const opts: LocationOption[] = []
    for (const [country] of countries)
      opts.push({ value: `country:${country}`, label: country, type: "country", country })
    for (const [city, { country }] of cities)
      opts.push({ value: `city:${city}`, label: city, type: "city", country })
    return opts.sort((a, b) => a.country.localeCompare(b.country) || a.label.localeCompare(b.label))
  }, [data])

  // ── Client-side filter ───────────────────────────────────────────────────────
  const items = useMemo<ShipmentMilestoneRow[]>(() => {
    if (!data) return []
    return data.filter(r => {
      if (r.status === "cancelado") return false
      if (!matchesLocation(r.senderCityName, r.senderCountryName, filterOrigins)) return false
      if (!matchesLocation(r.recipientCityName, r.recipientCountryName, filterDestinations)) return false
      if (search.trim()) {
        const term = search.toLowerCase()
        const hit  =
          r.senderName?.toLowerCase().includes(term) ||
          r.senderCityName?.toLowerCase().includes(term) ||
          r.senderCountryName?.toLowerCase().includes(term) ||
          r.recipientName?.toLowerCase().includes(term) ||
          r.recipientCityName?.toLowerCase().includes(term) ||
          r.recipientCountryName?.toLowerCase().includes(term) ||
          r.clientName?.toLowerCase().includes(term) ||
          r.driverNames?.toLowerCase().includes(term) ||
          String(r.internalId).includes(term)
        if (!hit) return false
      }
      return true
    })
  }, [data, search, filterOrigins, filterDestinations])

  const hasActiveFilter = !!(dateFrom || dateTo || filterOrigins.length > 0 || filterDestinations.length > 0)

  // ── Mutation ─────────────────────────────────────────────────────────────────
  const milestoneMut = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Record<string, string | null> }) => {
      const res = await authorizedFetch(`${API_URL}/api/shipments/${id}`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(updates),
      })
      return res.json()
    },
    onError: () => {
      toast.error("Error al actualizar el hito")
      qc.invalidateQueries({ queryKey: QUERY_KEY })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY })
    },
  })

  function handleMilestoneChangeRequest(id: string, updates: Record<string, string | null>) {
    setPendingMilestoneChange({
      id,
      updates,
      dateTime: toLocalDateTimeInputValue(new Date()),
    })
  }

  function handleMilestoneChange(id: string, updates: Record<string, string | null>) {
    const row = qc.getQueryData<ShipmentMilestoneRow[]>([...QUERY_KEY, dateFrom, dateTo])?.find(r => r.id === id)

    // Automatic status transitions (forward)
    if (updates.originArrivalAt === null || Object.values(updates).every(v => v === null)) {
      updates = { ...updates, status: "planificado" }
    }
    if (updates.originArrivalAt && updates.originArrivalAt !== null) updates = { ...updates, status: "activo" }
    if (updates.originLoadingAt && updates.originLoadingAt !== null) updates = { ...updates, status: "en_curso" }
    if (updates.finalizedAt     && updates.finalizedAt     !== null) updates = { ...updates, status: "finalizado" }

    // Implicit fields
    for (const { trigger, implicits } of IMPLICIT_FIELDS) {
      if (trigger in updates) {
        if (updates[trigger] !== null) {
          for (const implicit of implicits) {
            if (row && !row[implicit as keyof ShipmentMilestoneRow]) {
              updates = { ...updates, [implicit]: updates[trigger] ?? null }
            }
          }
        } else {
          for (const implicit of implicits) {
            updates = { ...updates, [implicit]: null }
          }
        }
      }
    }

    qc.setQueryData<ShipmentMilestoneRow[]>(
      [...QUERY_KEY, dateFrom, dateTo],
      prev => prev ? prev.map(r => r.id === id ? { ...r, ...updates } : r) : prev,
    )
    milestoneMut.mutate({ id, updates })
  }

  function confirmPendingMilestoneChange(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!pendingMilestoneChange) return

    const selectedDate = new Date(pendingMilestoneChange.dateTime)
    if (!pendingMilestoneChange.dateTime || Number.isNaN(selectedDate.getTime())) {
      toast.error("Ingresá una fecha y hora válidas")
      return
    }

    const timestamp = selectedDate.toISOString()
    const updates = Object.fromEntries(
      Object.entries(pendingMilestoneChange.updates).map(([key, value]) => [
        key,
        value === null ? null : timestamp,
      ]),
    ) as Record<string, string | null>

    setPendingMilestoneChange(null)
    handleMilestoneChange(pendingMilestoneChange.id, updates)
  }

  function renderCard(row: ShipmentMilestoneRow, dragHandle: React.ReactNode) {
    return (
      <InternacionalShipmentCard
        row={row as unknown as InternacionalCardRow}
        dragHandle={dragHandle}
        milestones={INTERNACIONAL_MILESTONES}
        onView={(id: string) => window.open(`/shipments/${id}`, "_blank")}
      />
    )
  }

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <>
      <SiteHeader title="Cargas — Internacional" />

      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6 space-y-4">

        {/* ── Toolbar ── */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative w-72 shrink-0">
            <SearchIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar origen, destino, cliente..."
              className="pl-8"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <span className="text-xs text-muted-foreground ml-auto">
            {isLoading ? "Cargando..." : `${items.length} envío${items.length !== 1 ? "s" : ""}`}
          </span>
        </div>

        {/* ── Filter bar ── */}
        <div className="flex flex-col gap-2 rounded-lg border bg-muted/40 px-3 py-2.5">

          {/* Row 1: date range + location filters + clear */}
          <div className="flex flex-wrap items-end gap-3">

            {/* Dates */}
            <div className="flex items-end gap-2">
              <div className="flex flex-col gap-1">
                <Label className="text-xs text-muted-foreground">Desde</Label>
                <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-7 text-xs w-36" />
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-xs text-muted-foreground">Hasta</Label>
                <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-7 text-xs w-36" />
              </div>
            </div>

            <div className="w-px self-stretch bg-border shrink-0" />

            {/* Origin */}
            <div className="flex flex-col gap-1 w-52">
              <Label className="text-xs text-muted-foreground">Origen</Label>
              <MultiLocationSelect
                options={originOptions}
                selected={filterOrigins}
                onChange={setFilterOrigins}
                placeholder="País o ciudad..."
              />
            </div>

            {/* Switch origin ↔ destination */}
            <button
              type="button"
              onClick={() => {
                const tmp = filterOrigins
                setFilterOrigins(filterDestinations)
                setFilterDestinations(tmp)
              }}
              title="Invertir origen y destino"
              className="self-end mb-0.5 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <ArrowLeftRightIcon className="size-4" />
            </button>

            {/* Destination */}
            <div className="flex flex-col gap-1 w-52">
              <Label className="text-xs text-muted-foreground">Destino</Label>
              <MultiLocationSelect
                options={destinationOptions}
                selected={filterDestinations}
                onChange={setFilterDestinations}
                placeholder="País o ciudad..."
              />
            </div>

            {hasActiveFilter && (
              <Button
                variant="ghost" size="sm"
                onClick={() => { setDateFrom(""); setDateTo(""); setFilterOrigins([]); setFilterDestinations([]) }}
                className="h-7 text-xs gap-1.5 text-muted-foreground hover:text-foreground self-end"
              >
                <XIcon className="size-3" /> Limpiar
              </Button>
            )}

          </div>

          {/* Selected chips summary */}
          {(filterOrigins.length > 0 || filterDestinations.length > 0) && (
            <div className="flex flex-wrap gap-1.5 pt-0.5">
              {filterOrigins.map(v => {
                const opt = originOptions.find(o => o.value === v)
                return (
                  <span key={v} className="inline-flex items-center gap-1 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 px-2 py-0.5 text-xs font-medium">
                    {opt?.type === "country" ? <GlobeIcon className="size-2.5" /> : <MapPinIcon className="size-2.5" />}
                    {opt?.label ?? v}
                    <button type="button" onClick={() => setFilterOrigins(p => p.filter(x => x !== v))} className="hover:text-blue-900 ml-0.5">×</button>
                  </span>
                )
              })}
              {filterDestinations.map(v => {
                const opt = destinationOptions.find(o => o.value === v)
                return (
                  <span key={v} className="inline-flex items-center gap-1 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 px-2 py-0.5 text-xs font-medium">
                    {opt?.type === "country" ? <GlobeIcon className="size-2.5" /> : <MapPinIcon className="size-2.5" />}
                    {opt?.label ?? v}
                    <button type="button" onClick={() => setFilterDestinations(p => p.filter(x => x !== v))} className="hover:text-purple-900 ml-0.5">×</button>
                  </span>
                )
              })}
            </div>
          )}

        </div>

        {/* ── Kanban ── */}
        {isLoading ? (
          <div className="flex flex-1 items-center justify-center text-muted-foreground py-24">Cargando cargas...</div>
        ) : items.length === 0 ? (
          <div className="flex flex-1 items-center justify-center text-muted-foreground py-24">No se encontraron cargas internacionales.</div>
        ) : (
          <MilestoneKanban
            items={items}
            milestones={INTERNACIONAL_MILESTONES}
            showPending={false}
            renderCard={renderCard}
            onView={id => navigate(`/shipments/${id}`)}
            onMilestoneChange={handleMilestoneChange}
            onMilestoneChangeRequest={handleMilestoneChangeRequest}
          />
        )}

      </div>

      <Dialog
        open={!!pendingMilestoneChange}
        onOpenChange={open => { if (!open) setPendingMilestoneChange(null) }}
      >
        <DialogContent>
          <form onSubmit={confirmPendingMilestoneChange}>
            <DialogHeader>
              <DialogTitle>Confirmar movimiento</DialogTitle>
              <DialogDescription>
                Elegí la fecha y hora del cambio. La carga se moverá de columna al guardar.
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-1.5">
              <Label htmlFor="milestone-change-date">Fecha y hora</Label>
              <Input
                id="milestone-change-date"
                type="datetime-local"
                required
                autoFocus
                value={pendingMilestoneChange?.dateTime ?? ""}
                onChange={event => setPendingMilestoneChange(current => current
                  ? { ...current, dateTime: event.target.value }
                  : current)}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPendingMilestoneChange(null)}>
                Cancelar
              </Button>
              <Button type="submit">Guardar y mover</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  )
}
